import { useEffect, useState } from "react";
import { Bell, BellOff, CheckCircle2, Clock, Loader2, Mail, MonitorSmartphone } from "lucide-react";
import { api, apiErrorDetail } from "@/lib/api";

function Toggle({ id, checked, onChange, testid }) {
  return (
    <button
      id={id}
      role="switch"
      aria-checked={checked}
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className={`relative w-12 h-7 rounded-full transition-colors duration-300 shrink-0 ${checked ? "bg-deeptaupe" : "bg-taupe/40"}`}
    >
      <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow transition-transform duration-300 ${checked ? "translate-x-5" : ""}`} />
    </button>
  );
}

/* Centro notifiche: cosa vuole ricevere lo studio, con anteprime senza dati sensibili. */
export default function NotificationsTab({ flash }) {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [browserState, setBrowserState] = useState("default");

  useEffect(() => {
    api
      .get("/admin/notifications")
      .then(({ data }) => setPrefs(data.prefs))
      .catch((e) => flash(apiErrorDetail(e), false));
    if ("Notification" in window) setBrowserState(Notification.permission);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (section, field, value) => setPrefs({ ...prefs, [section]: { ...prefs[section], [field]: value } });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/notifications", { prefs });
      setPrefs(data.prefs);
      flash("Preferenze di notifica salvate");
    } catch (e) {
      flash(apiErrorDetail(e), false);
    } finally {
      setBusy(false);
    }
  };

  const askBrowser = async () => {
    if (!("Notification" in window)) {
      flash("Questo browser non supporta le notifiche", false);
      return;
    }
    const p = await Notification.requestPermission();
    setBrowserState(p);
    if (p === "granted") flash("Notifiche del browser attive");
    if (p === "denied") flash("Le notifiche del browser sono bloccate nelle impostazioni del browser", false);
  };

  const testBrowser = () => {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("K Nutrition", { body: "Notifica di prova: quando arriva una richiesta vedrai questo avviso." });
    }
  };

  if (!prefs) return <p className="text-charcoal/50 text-sm py-10">Caricamento…</p>;

  const rows = [
    {
      key: "newRequest",
      title: "Nuove richieste di appuntamento",
      desc: "Appena un paziente prenota: email immediata con i dettagli e pulsante WhatsApp; avviso del browser senza dati sensibili.",
      fields: [["email", "Email allo studio"], ["browser", "Notifica del browser"]],
      icons: [Mail, MonitorSmartphone],
    },
    {
      key: "expiryWarning",
      title: "Avviso richieste in scadenza",
      desc: "Ti avvisiamo prima che una richiesta non confermata scada e l'orario si liberi.",
      fields: [["email", "Email allo studio"], ["hoursBefore", "Ore prima della scadenza"]],
      icons: [Bell, Clock],
    },
    {
      key: "expired",
      title: "Richieste scadute",
      desc: "Riepilogo delle richieste scadute senza conferma, con gli orari liberati.",
      fields: [["email", "Email allo studio"]],
      icons: [BellOff],
    },
    {
      key: "dailySummary",
      title: "Riepilogo giornaliero",
      desc: "Ogni mattina alle 7:45, l'elenco degli appuntamenti di oggi con stato.",
      fields: [["email", "Email allo studio"]],
      icons: [Mail],
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl lg:text-3xl flex items-center gap-3">
          <Bell size={24} className="text-deeptaupe" /> Notifiche
        </h2>
        <p className="text-sm text-charcoal/60 mt-2 max-w-2xl">
          Scegli cosa vuoi ricevere e come. Le notifiche del browser mostrano solo un avviso generico,
          senza i dati dei pazienti; le email allo studio contengono i dettagli per gestire le richieste.
        </p>
      </div>

      <div className="bg-white rounded-[24px] shadow-soft p-6 space-y-5" data-testid="notifications-form">
        {rows.map((r) => (
          <div key={r.key} className="pb-5 border-b border-taupe/15 last:border-b-0 last:pb-0" data-testid={`notif-section-${r.key}`}>
            <p className="font-medium text-sm mb-1">{r.title}</p>
            <p className="text-[13px] text-charcoal/60 mb-3">{r.desc}</p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8">
              {r.fields.map(([field, label], i) => {
                const Icon = r.icons[i];
                if (field === "hoursBefore") {
                  return (
                    <label key={field} className="flex items-center gap-3 text-[13px]" htmlFor={`nh-${r.key}`}>
                      <Icon size={15} className="text-taupe" /> Avviso ore prima della scadenza:
                      <input
                        id={`nh-${r.key}`}
                        type="number"
                        min="1"
                        max="24"
                        value={prefs[r.key].hoursBefore}
                        onChange={(e) => set(r.key, field, parseInt(e.target.value || "2", 10))}
                        data-testid={`notif-hours-${r.key}`}
                        className="w-16 rounded-xl border border-taupe/40 px-3 py-2 text-sm bg-transparent"
                      />
                    </label>
                  );
                }
                return (
                  <span key={field} className="flex items-center gap-2.5 text-[13px]">
                    <Icon size={15} className="text-taupe" /> {label}
                    <Toggle
                      checked={!!prefs[r.key][field]}
                      onChange={(v) => set(r.key, field, v)}
                      testid={`notif-${r.key}-${field}`}
                    />
                  </span>
                );
              })}
            </div>
          </div>
        ))}

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button
            onClick={save}
            disabled={busy}
            data-testid="notifications-save"
            className="inline-flex items-center gap-2 rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-8 py-3.5 hover:bg-charcoal transition-colors disabled:opacity-60"
          >
            {busy && <Loader2 size={14} className="animate-spin" />} Salva preferenze
          </button>
          {browserState === "granted" ? (
            <button onClick={testBrowser} data-testid="notifications-browser-test" className="inline-flex items-center gap-2 rounded-full border border-deeptaupe/40 text-deeptaupe text-[12px] uppercase tracking-widest2 font-medium px-5 py-3 hover:bg-peach transition-colors">
              <CheckCircle2 size={14} /> Notifiche browser attive — prova
            </button>
          ) : (
            <button onClick={askBrowser} data-testid="notifications-browser-enable" className="inline-flex items-center gap-2 rounded-full border border-taupe/40 text-charcoal/70 text-[12px] uppercase tracking-widest2 font-medium px-5 py-3 hover:bg-peach transition-colors">
              <MonitorSmartphone size={14} /> {browserState === "denied" ? "Notifiche browser bloccate" : "Attiva notifiche del browser"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
